import React, { useState, useEffect, useRef } from 'react';
import { fetchRelevantAwardsForMonthlyZip } from '../../utilityFunctions/supabaseUtilities';
import AwardsInsights from './InsightsTab';
import RawAwardsView from './RawAwardsTab';
import DownArrow from '../../assets/down-arrow.png';
import AwardSizeFilter from './AwardSizeFilter';
import FunderStateFilter from './FunderStateFilter';

function AwardsSearch({
    currentProgram,
    setShowManageProgramModal,
    setShowLoadingModal,    
    setShowChooseProgramModal,    
    setShowLoginModal,
    membershipDetails,
    setShowPaywallModal,
}) {    

    const [selectedTabView, setSelectedTabView] = useState("insights");

    // Query Parameters - Updated to date range
    const [startDate, setStartDate] = useState("2025-05"); // Format: YYYY-MM
    const [endDate, setEndDate] = useState("2025-05"); // Format: YYYY-MM

    // Awards Search Results
    const [fetchedAwardsForNteeMajorNationally, setFetchedAwardsForNteeMajorNationally] = useState([]);    
    const [funderCountForNteeMajorNationally, setFunderCountForNteeMajorNationally] = useState(0);
        
    // Tooltip State
    const [showStateTooltip, setShowStateTooltip] = useState(false);
    const [showNteeTooltip, setShowNteeTooltip] = useState(false);
    const [stateTooltipTimeout, setStateTooltipTimeout] = useState(null);
    const [nteeTooltipTimeout, setNteeTooltipTimeout] = useState(null);

    // KEEP these state variables - they're needed for the API call
    const [appliedAwardSizeMin, setAppliedAwardSizeMin] = useState(0);
    const [appliedAwardSizeMax, setAppliedAwardSizeMax] = useState(1000000);
    const [appliedFunderState, setAppliedFunderState] = useState('');

    useEffect(() => {
        if (currentProgram) {
            fetchAwardsForNteeMajorNationally();
        }
    }, [currentProgram, startDate, endDate, appliedAwardSizeMin, appliedAwardSizeMax, appliedFunderState]);
    
    useEffect(() => {
        if (fetchedAwardsForNteeMajorNationally.length > 0) {
            setFunderCountForNteeMajorNationally(new Set(fetchedAwardsForNteeMajorNationally.map(award => award.funder_ein)).size);
        }
    }, [fetchedAwardsForNteeMajorNationally]);
    

    const fetchAwardsForNteeMajorNationally = async () => {
        setShowLoadingModal(true);
        
        let allData = [];
        let currentPage = 0;
        let hasMoreData = true;
        
        while (hasMoreData) {
            const data = await fetchRelevantAwardsForMonthlyZip(
                startDate,
                endDate,
                currentProgram?.nteeFull,
                currentProgram?.primaryState, 
                appliedAwardSizeMin || null,
                appliedAwardSizeMax || null,                
                currentPage, // page
                500 // pageSize
            );
            
            // Filter by funder state if applied
            let filteredData = data;
            if (appliedFunderState && appliedFunderState !== '') {
                filteredData = data.filter(award => 
                    award.form_990 && award.form_990.state_abbreviation_cd === appliedFunderState
                );
            }
            
            allData = [...allData, ...filteredData];
            hasMoreData = data.length === 500; // If we got 500, there might be more
            currentPage++;
        }
        
        console.log("Fetched all awards for NTEE major nationally: ", allData);
        setFetchedAwardsForNteeMajorNationally(allData);
        setShowLoadingModal(false);
    }

    // Add these handler functions
    const handleAwardSizeApply = (min, max) => {
        setAppliedAwardSizeMin(min);
        setAppliedAwardSizeMax(max);
    };

    const handleAwardSizeReset = () => {
        setAppliedAwardSizeMin(0);
        setAppliedAwardSizeMax(1000000);
    };

    const handleFunderStateApply = (state) => {
        setAppliedFunderState(state);
    };

    const handleFunderStateReset = () => {
        setAppliedFunderState('');
    };

    const handleStartDateSelected = (selectedValue) => {
        if (membershipDetails) {
            // User has membership, allow all dates
            setStartDate(selectedValue);
        } else {
            // Free user - only allow February, March, April
            const allowedDates = ['2025-03', '2025-04', '2025-05'];
            if (allowedDates.includes(selectedValue)) {
                setStartDate(selectedValue);
            } else {
                setShowPaywallModal(true);
            }
        }
    };

    const handleEndDateSelected = (selectedValue) => {
        if (membershipDetails) {
            // User has membership, allow all dates
            setEndDate(selectedValue);
        } else {
            // Free user - only allow February, March, April
            const allowedDates = ['2025-03', '2025-04', '2025-05'];
            if (allowedDates.includes(selectedValue)) {
                setEndDate(selectedValue);
            } else {
                setShowPaywallModal(true);
            }
        }
    };

    return (
        <div className="p-4 sm:p-8 lg:p-12 text-left">
            
            {/* Primary Search Criteria */}
            <div className='bg-indigo-100 text-center p-4 sm:p-6 lg:p-8 rounded-md gap-2 mb-6 sm:mb-8 border border-gray-500'>   
                {/* Program Selection */}
                <div className='flex flex-col gap-3 sm:gap-4 justify-center'>
                    {/* Line 1 - Mobile responsive stats */}
                    <div className='flex flex-col sm:flex-row gap-1 sm:gap-2 justify-center items-center'>
                        <div className='flex flex-row gap-1 sm:gap-2 items-center'>
                            <span>
                                <p className='text-xl sm:text-2xl lg:text-3xl font-bold text-indigo-500'>
                                {funderCountForNteeMajorNationally.toLocaleString()}</p>
                            </span>
                            <p className='text-xl sm:text-2xl lg:text-3xl text-gray-500'>funders awarded</p>
                        </div>
                        <div className='flex flex-row gap-1 sm:gap-2 items-center'>
                            <span>
                                <p className='text-xl sm:text-2xl lg:text-3xl font-bold text-indigo-500'>
                                {fetchedAwardsForNteeMajorNationally.length.toLocaleString()}</p>
                            </span>
                            <p className='text-xl sm:text-2xl lg:text-3xl text-gray-500'>grants</p>
                        </div>
                    </div>
                    
                    {/* Line 2 - Mobile responsive program selection */}
                    <div className='flex flex-col sm:flex-row gap-2 justify-center items-center'>
                        <p className='text-lg sm:text-2xl lg:text-3xl text-gray-500'>to nonprofits like</p>
                        <span 
                            className='shadow-md bg-white border border-gray-500 text-black px-3 py-2 rounded-md font-bold flex flex-row gap-2 items-center cursor-pointer text-sm sm:text-base' 
                            onClick={() => setShowChooseProgramModal(true)}
                        >
                            <span className="truncate max-w-[200px] sm:max-w-none">{currentProgram?.programName}</span>
                            <img src={DownArrow} alt="Down Arrow" className='w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0' />
                        </span>
                    </div>
                    
                    {/* Line 3 - Mobile responsive date range */}
                    <div className='flex flex-col sm:flex-row gap-2 justify-center items-center'>
                        <p className='text-lg sm:text-2xl lg:text-3xl text-gray-500'>reported from</p>
                        <div className='flex flex-col sm:flex-row gap-1 sm:gap-2 items-center'>
                            <select 
                                className="shadow-md bg-white border border-gray-500 text-black px-2 py-1 rounded-md font-bold text-sm sm:text-base"
                                value={startDate}
                                onChange={(e) => handleStartDateSelected(e.target.value)}
                            >
                                <option value="2024-06">🔒 June 2024</option>
                                <option value="2024-07">🔒 July 2024</option>
                                <option value="2024-08">🔒 August 2024</option>
                                <option value="2024-09">🔒 September 2024</option>
                                <option value="2024-10">🔒 October 2024</option>
                                <option value="2024-11">🔒 November 2024</option>
                                <option value="2024-12">🔒 December 2024</option>
                                <option value="2025-01">🔒 January 2025</option>
                                <option value="2025-02">🔒 February 2025</option>
                                <option value="2025-03">March 2025</option>
                                <option value="2025-04">April 2025</option>
                                <option value="2025-05">May 2025</option>
                            </select>
                            <p className='text-lg sm:text-2xl lg:text-3xl text-gray-500'>to</p>
                            <select 
                                className="shadow-md bg-white border border-gray-500 text-black px-2 py-1 rounded-md font-bold text-sm sm:text-base"
                                value={endDate}
                                onChange={(e) => handleEndDateSelected(e.target.value)}
                            >
                                <option value="2024-06">🔒 June 2024</option>
                                <option value="2024-07">🔒 July 2024</option>
                                <option value="2024-08">🔒 August 2024</option>
                                <option value="2024-09">🔒 September 2024</option>
                                <option value="2024-10">🔒 October 2024</option>
                                <option value="2024-11">🔒 November 2024</option>
                                <option value="2024-12">🔒 December 2024</option>
                                <option value="2025-01">🔒 January 2025</option>
                                <option value="2025-02">🔒 February 2025</option>
                                <option value="2025-03">March 2025</option>
                                <option value="2025-04">April 2025</option>
                                <option value="2025-05">May 2025</option>
                            </select>
                        </div>
                    </div>
                </div>                    
            </div>
            
            {/* Awards Search Criteria - Mobile responsive filters */}
            <div className='flex flex-col sm:flex-row gap-2 mt-4 text-xs relative'>                
                {
                    currentProgram?.primaryState && (
                        <div className="relative inline-block">
                            <span 
                                className='shadow-sm bg-gray-100 text-gray-500 border border-gray-500 px-3 py-2 rounded-md flex flex-row gap-2 items-center cursor-not-allowed text-xs'
                                onMouseEnter={() => {
                                    if (stateTooltipTimeout) clearTimeout(stateTooltipTimeout);
                                    setShowStateTooltip(true);
                                }}
                                onMouseLeave={() => {
                                    const timeout = setTimeout(() => setShowStateTooltip(false), 100);
                                    setStateTooltipTimeout(timeout);
                                }}
                            >
                                <span className="truncate">State: {currentProgram?.primaryState}</span>
                                <img src={DownArrow} alt="Down Arrow" className='w-3 h-3 flex-shrink-0' />
                            </span>
                            {showStateTooltip && (
                                <div 
                                    className="absolute top-full left-0 mt-1 bg-gray-800 text-white text-xs rounded-md p-2 z-20 w-max min-w-full whitespace-normal"
                                    onMouseEnter={() => {
                                        if (stateTooltipTimeout) clearTimeout(stateTooltipTimeout);
                                        setShowStateTooltip(true);
                                    }}
                                    onMouseLeave={() => {
                                        const timeout = setTimeout(() => setShowStateTooltip(false), 100);
                                        setStateTooltipTimeout(timeout);
                                    }}
                                >
                                    Defined by your selected program. <span 
                                        className="text-indigo-300 cursor-pointer hover:text-indigo-100"
                                        onClick={() => setShowChooseProgramModal(true)}
                                    >
                                        Change
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                }
                {
                    currentProgram?.nteeFull && (
                        <div className="relative inline-block">
                            <span 
                                className='shadow-sm bg-gray-100 text-gray-500 border border-gray-500 px-3 py-2 rounded-md flex flex-row gap-2 items-center cursor-not-allowed text-xs'
                                onMouseEnter={() => {
                                    if (nteeTooltipTimeout) clearTimeout(nteeTooltipTimeout);
                                    setShowNteeTooltip(true);
                                }}
                                onMouseLeave={() => {
                                    const timeout = setTimeout(() => setShowNteeTooltip(false), 100);
                                    setNteeTooltipTimeout(timeout);
                                }}
                            >
                                <span className="truncate max-w-[150px] sm:max-w-none">NTEE: {currentProgram?.nteeFull}</span>
                                <img src={DownArrow} alt="Down Arrow" className='w-3 h-3 flex-shrink-0' />
                            </span>
                            {showNteeTooltip && (
                                <div 
                                    className="absolute top-full left-0 mt-1 bg-gray-800 text-white text-xs rounded-md p-2 z-20 w-max min-w-full whitespace-normal"
                                    onMouseEnter={() => {
                                        if (nteeTooltipTimeout) clearTimeout(nteeTooltipTimeout);
                                        setShowNteeTooltip(true);
                                    }}
                                    onMouseLeave={() => {
                                        const timeout = setTimeout(() => setShowNteeTooltip(false), 100);
                                        setNteeTooltipTimeout(timeout);
                                    }}
                                >
                                    Defined by your selected program. <span 
                                        className="text-indigo-300 cursor-pointer hover:text-indigo-100"
                                        onClick={() => setShowChooseProgramModal(true)}
                                    >
                                        Change
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                }
                <div className="relative">
                    <AwardSizeFilter
                        appliedAwardSizeMin={appliedAwardSizeMin}
                        appliedAwardSizeMax={appliedAwardSizeMax}
                        onApply={handleAwardSizeApply}
                        onReset={handleAwardSizeReset}
                    />
                </div>
                <div className="relative">
                    <FunderStateFilter
                        appliedFunderState={appliedFunderState}
                        onApply={handleFunderStateApply}
                        onReset={handleFunderStateReset}
                    />
                </div>             
            </div>

            {/* View Mode Selector - Mobile responsive */}
            <div className='flex flex-row mt-4 sm:mt-6 text-base sm:text-lg w-full justify-center'>
                <button className={`w-1/2 border border-gray-500 bg-indigo-100 text-gray-500 px-3 py-2 sm:px-4 sm:py-3 rounded-l-md text-sm sm:text-base ${selectedTabView === "insights" ? "bg-indigo-500 text-white font-bold" : ""}`}
                    onClick={() => setSelectedTabView("insights")}
                >
                    Insights
                </button>
                <button className={`w-1/2 border border-gray-500 bg-indigo-100 text-gray-500 px-3 py-2 sm:px-4 sm:py-3 rounded-r-md text-sm sm:text-base ${selectedTabView === "awards" ? "bg-indigo-500 text-white font-bold" : ""}`}
                    onClick={() => setSelectedTabView("awards")}
                >
                    Awards
                </button>
            </div>

            {selectedTabView === "insights" && (
                <AwardsInsights 
                    currentProgram={currentProgram}
                    fetchedAwardsForNteeMajorNationally={fetchedAwardsForNteeMajorNationally}
                    setShowManageProgramModal={setShowManageProgramModal}
                    setShowLoginModal={setShowLoginModal}
                />
            )}

            {selectedTabView === "awards" && (
                <RawAwardsView 
                    rawAwards={fetchedAwardsForNteeMajorNationally}
                />
            )}                
        </div>
    );
}

export default AwardsSearch;